import React from "react";

/**
 * AXE MARKETING SERVICES LLP — text-based wordmark.
 *
 * Layout:
 *   ┌─────┐ │ MARKETING
 *   │ AXE │ │ ───────────
 *   └─╲───┘ │ SERVICES LLP
 *           └─ red underline
 *
 *  - "AXE" heaviest sans-serif, white
 *  - Red (#E02424) diagonal slash through the X (top-right → bottom-left) via SVG polygon
 *  - Thin vertical divider between wordmark + descriptor
 *  - "MARKETING" bold electric blue (#1A56DB), wide-tracked caps
 *  - "SERVICES LLP" light sky blue (#60A5FA), wide-tracked caps
 *  - 1.5px red underline beneath the descriptor block
 */
export default function Logo({ size = "md", className = "" }) {
  const scales = {
    sm: { axe: "text-xl", desc: "text-[9px]", sub: "text-[8px]" },
    md: { axe: "text-2xl sm:text-3xl", desc: "text-[10px] sm:text-[11px]", sub: "text-[9px] sm:text-[10px]" },
    lg: { axe: "text-4xl", desc: "text-[13px]", sub: "text-[11px]" },
  };
  const s = scales[size] || scales.md;

  return (
    <span
      className={`inline-flex items-stretch gap-3 sm:gap-3.5 select-none ${className}`}
      data-testid="axe-logo"
      aria-label="Axe Marketing Services LLP"
    >
      {/* Wordmark — AXE with the X's TR→BL diagonal stroke painted red */}
      <span className="relative inline-flex items-center">
        <span
          className={`font-display font-bold uppercase tracking-tight text-white leading-none ${s.axe}`}
          style={{ fontWeight: 900, letterSpacing: "-0.03em" }}
        >
          A
          <span className="relative inline-block">
            {/* Base white X */}
            <span aria-hidden="false">X</span>
            {/* Red overlay: same X glyph, clipped to the TR→BL diagonal stroke band only */}
            <span
              aria-hidden="true"
              className="absolute inset-0 text-[#E02424] pointer-events-none"
              style={{
                clipPath:
                  "polygon(72% 0%, 100% 0%, 100% 18%, 28% 100%, 0% 100%, 0% 82%)",
                filter: "drop-shadow(0 0 4px rgba(224,36,36,0.55))",
              }}
            >
              X
            </span>
          </span>
          E
        </span>
      </span>

      {/* Thin vertical divider */}
      <span
        aria-hidden="true"
        className="w-px self-stretch bg-white/20"
      />

      {/* Descriptor block */}
      <span className="relative flex flex-col justify-center leading-none">
        <span
          className={`font-display font-bold uppercase text-[#1A56DB] ${s.desc}`}
          style={{ letterSpacing: "0.32em", fontWeight: 800 }}
        >
          Marketing
        </span>
        <span
          className={`mt-1 font-display uppercase text-[#60A5FA] ${s.sub}`}
          style={{ letterSpacing: "0.28em", fontWeight: 300 }}
        >
          Services&nbsp;LLP
        </span>
        {/* 1.5px red underline beneath the descriptor block (85% of descriptor width) */}
        <span
          aria-hidden="true"
          className="absolute left-0 w-[85%] -bottom-1.5 h-[1.5px] bg-[#E02424]"
        />
      </span>
    </span>
  );
}
